import { PDFDocument } from 'pdf-lib';
import { toast } from 'react-toastify';
import appApi from '../../../../../../api/app-api';
import { displayErrorText } from '../../../../../../api/errors/api-error-codes';
import { AccountingDepositDetailsEntity } from '../../../../../../api/types/accounting/accounting-deposit-details-entity';
import { AccountingDepositPaymentDetailsEntity } from '../../../../../../api/types/accounting/accounting-deposit-payment-details-entity';
import { formatDate } from '../../../../../../utils/date-format';

// Pull every payment in the deposit so we don't miss any receipts on later pages.
const EXPORT_LIMIT = 100000;

const fetchPdfBytes = async (url: string): Promise<Uint8Array | null> => {
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) return null;
    const buffer = await res.arrayBuffer();
    return new Uint8Array(buffer);
  } catch {
    return null;
  }
};

const triggerDownload = (bytes: Uint8Array, fileName: string) => {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileName}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Defer revoke so the browser has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const exportDepositReceiptsPdfOperation = async (
  depositId: number,
  depositDetails: AccountingDepositDetailsEntity | null,
  setExporting: (exporting: boolean) => void,
  // Optional subset filter. Empty or omitted → export every payment in the
  // deposit (legacy behavior). Non-empty → restrict the merge to payments
  // whose `id` is in this list, regardless of which page they live on.
  selectedPaymentIds?: number[],
) => {
  try {
    setExporting(true);

    const res = await appApi.getAccountingDepositById(
      depositId,
      0,
      EXPORT_LIMIT,
    );

    if (res.data.errCode) {
      toast.warn(displayErrorText(res.data.errCode));
      setExporting(false);
      return;
    }

    const loadedDeposit = res.data.body?.deposit ?? null;
    const allPayments: AccountingDepositPaymentDetailsEntity[] =
      res.data.body?.payments?.data ?? [];

    const selectedIdSet =
      selectedPaymentIds && selectedPaymentIds.length > 0
        ? new Set(selectedPaymentIds)
        : null;
    const payments = selectedIdSet
      ? allPayments.filter((p) => selectedIdSet.has(p.id))
      : allPayments;

    const urls = payments
      .map((p) => p.accountingDocument?.fileUrl)
      .filter((u): u is string => typeof u === 'string' && u.trim().length > 0);

    if (urls.length === 0) {
      toast.warn(
        selectedIdSet
          ? 'לא נמצאו קבלות לייצוא עבור התשלומים שנבחרו'
          : 'לא נמצאו קבלות לייצוא בהפקדה זו',
      );
      setExporting(false);
      return;
    }

    const merged = await PDFDocument.create();
    let failed = 0;

    for (const url of urls) {
      const bytes = await fetchPdfBytes(url);
      if (!bytes) {
        failed += 1;
        continue;
      }
      try {
        const src = await PDFDocument.load(bytes, { ignoreEncryption: true });
        const pages = await merged.copyPages(src, src.getPageIndices());
        pages.forEach((page) => merged.addPage(page));
      } catch {
        failed += 1;
      }
    }

    if (merged.getPageCount() === 0) {
      toast.error('לא ניתן היה לטעון אף קבלה (ייתכן וחסומות ע״י CORS)');
      setExporting(false);
      return;
    }

    const mergedBytes = await merged.save();

    const depositIdForFile =
      depositDetails?.id ?? loadedDeposit?.id ?? depositId;
    const depositDate =
      depositDetails?.depositDate ?? loadedDeposit?.depositDate;
    const depositDateStr = depositDate
      ? formatDate(depositDate as Date).replace(/\//g, '-')
      : '';
    const fileName = `קבלות הפקדה ${depositIdForFile}${depositDateStr ? ` - ${depositDateStr}` : ''}`;

    triggerDownload(mergedBytes, fileName);

    if (failed > 0) {
      toast.warn(`${failed} קבלות לא נטענו והושמטו מהקובץ המאוחד`);
    } else {
      toast.success('הקבלות אוחדו והורדו בהצלחה');
    }

    setExporting(false);
  } catch (e) {
    toast.error('משהו קרה, יש לנסות שוב בעוד מספר רגעים');
    setExporting(false);
  }
};
